import React, { FormEvent, useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, TableContainer, Stack, Paper, Typography,
  Collapse, Button
} from '@mui/material';
import { ISTableTitle, ISTableHeaderCell, ISTableRow, ISTableCell } from '../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { formattedNoMoneyCurrency } from '../../utils/StringUtils';
import DiscountList from '../../models/DiscountList';
import { useThemeContext } from '../../theme/ThemeContext';
import { ArrowDropDownOutlined, ArrowDropUpOutlined } from '@mui/icons-material';
import { getToken } from '../../utils/fetchApi';
import { FiletypeXls, FiletypePdf } from 'react-bootstrap-icons';
import moment from 'moment';

export interface Row {
  data: DiscountList;
  action?: 'create' | 'update' | 'error';
}

interface Props {
  rows: Row[];
  loading: boolean;
  fullInfo?: boolean;
  adminInfo?: boolean;
  codigo_cliente?: string;
}

const TableDiscountList: React.FC<Props> = (props) => {
  const { t } = useTranslation(['product', 'others', 'preorder']);
  const { theme } = useThemeContext();
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.adminInfo);

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const maxColumns = () => {
    let maxValue = 4;
    props.rows?.forEach(row => {
      const count = row.data.discountIndexes?.length || 0;
      if (count > maxValue) maxValue = count;
    });
    return maxValue;
  };

  const mapStyle = (discount: DiscountList, index: number) => {
    const isHighlighted = discount.highlightedIndexes?.includes(discount.discountIndexes[index]);
    return {
      fontWeight: isHighlighted ? 'bold' : 'inherit',
      color: isHighlighted ? theme.palette.success.main : 'inherit',
    };
  };

  const tooltipText = (discount: DiscountList, index: number) => {
    const isHighlighted = discount.highlightedIndexes?.includes(discount.discountIndexes[index]);
    return (
      <Paper elevation={2} sx={{ padding: "10px" }}>
        <Typography>
          {isHighlighted
            ? t('basicDiscount', { ns: 'others' })
            : t('extraDiscount', { ns: 'others' })}
        </Typography>
      </Paper>
    );
  };

  const CompleteHeaderCells: React.FC = () => {
    const mc = maxColumns();
    return (
      <>
        {Array.from({ length: mc }, (_, index) => (
          <ISTableHeaderCell align="left" sx={{ minWidth: '60px', maxWidth: '60px' }} key={index}>
            {(index === Math.floor(mc / 2)) ? t('discountList', { ns: 'others' }) : ''}
          </ISTableHeaderCell>
        ))}
      </>
    );
  };

  const DownloadDiscountListButton = ({ dProps }: any) => {
    const canShare = () => navigator.canShare?.();

    const handleDownloadAndShare = async (fileType: string) => {
      const url = dProps.adminInfo
        ? `${process.env.REACT_APP_API_URL}/dlDiscountList?access_token=${getToken()}&codigo_cliente=${dProps.codigo_cliente}&fileType=${fileType}`
        : `${process.env.REACT_APP_API_URL}/dlDiscountList?access_token=${getToken()}&fileType=${fileType}`;

      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileNameMatch = contentDisposition?.match(/filename=(.+)/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : `bonificaciones${dProps.codigo_cliente ? `-${dProps.codigo_cliente}` : ''}-${moment().tz("America/Argentina/Buenos_Aires").format('YYYY-MM-DD')}-industrias_saladillo.${fileType}`;

        const file = new File([blob], fileName, {
          type: fileType === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: t('downloadDL', { ns: 'others' }),
            text: t('shareDLText', { ns: 'others' }) || 'Compartir bonificaciones',
          });
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        console.error('Error al descargar/compartir:', error);
        window.open(url, '_blank');
      }
    };

    return (
      <Stack display="flex" flexDirection='row' justifyContent="flex-end" padding="10px" paddingTop="20px">
        <Button
          id="download"
          size='large'
          style={{ textTransform: 'none', marginRight: '30px' }}
          onClick={() => handleDownloadAndShare('pdf')}
        >
          {t(canShare() ? 'shareDLText' : 'downloadDL', { ns: 'others' })}&nbsp;&nbsp;<FiletypePdf size={30} />
        </Button>
        <Button
          id="download"
          size='large'
          style={{ textTransform: 'none' }}
          onClick={() => handleDownloadAndShare('xls')}
        >
          {t('downloadDL', { ns: 'others' })}&nbsp;&nbsp;<FiletypeXls size={30} />
        </Button>
      </Stack>
    );
  };

  const TableData: React.FC = () =>
    <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
      <Table size="medium" stickyHeader={!props.fullInfo || (props.fullInfo && !props.adminInfo)}>
        <TableHead>
          <TableRow selected>
            <ISTableHeaderCell align='center'>{t('family')}</ISTableHeaderCell>
            <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
            <CompleteHeaderCells />
            <ISTableHeaderCell>&nbsp;</ISTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!props.loading && props.rows.map((row, rowIndex) =>
            <ISTableRow key={rowIndex} color={mapActionToColor(row.action)}>
              <ISTableCell align='center'>{row.data.code}</ISTableCell>
              <ISTableCell>{row.data.description}</ISTableCell>

              {Array.from({ length: maxColumns() }, (_, i) => {
                const hasValue = i < (row.data.discountIndexes?.length || 0);
                const value = hasValue ? row.data[`discount_${i + 1}`] : null;

                return (
                  <ISTableCell
                    key={i}
                    tooltip={hasValue ? tooltipText(row.data, i) : undefined}
                    style={hasValue ? mapStyle(row.data, i) : {}}
                    align="right"
                  >
                    {hasValue && value != null ? formattedNoMoneyCurrency(value) : ''}
                  </ISTableCell>
                );
              })}

              <TableCell>&nbsp;</TableCell>
            </ISTableRow>
          )}
        </TableBody>
      </Table>

      {props.fullInfo && props.adminInfo && <DownloadDiscountListButton dProps={props} />}

      {props.loading &&
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={25} />
        </div>}
    </TableContainer>;

  return (
    <Stack
      style={{
        display: 'flex', flexDirection: 'column',
        height: props.fullInfo ? (!props.adminInfo ? 'calc(100% - var(--header-height)' : '') : 'calc(94% - var(--header-height)',
        borderRadius: "10px", margin: "10px", minWidth: props.fullInfo ? "500px" : "770px"
      }}
      onClick={(event: FormEvent) => {
        if ((event.target as HTMLInputElement).id !== 'download') {
          event.preventDefault();
          event.stopPropagation();
          props.adminInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true);
        }
      }}
    >
      {props.adminInfo &&
        <ISTableTitle title={t('discountList', { ns: 'others' })}
          sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack sx={{ display: "flex", flexDirection: "row" }}>
            {props.adminInfo && showFullInfo
              ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />
              : props.adminInfo
                ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />
                : <></>}
          </Stack>
        </ISTableTitle>}

      {props.adminInfo &&
        <Collapse style={{ padding: props.adminInfo ? "10px" : 'inherit' }} in={props.adminInfo ? showFullInfo : true}>
          <TableData />
        </Collapse>}

      {!props.adminInfo && <ISTableTitle title={t('discountList', { ns: 'others' })} />}
      {!props.adminInfo && <TableData />}
      {!props.adminInfo && <DownloadDiscountListButton dProps={props} />}
    </Stack>
  );
};

export default TableDiscountList;
